import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps, Iitem, IpromoCode, TbackEndGeneratedFields, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { Item } from './item.define';

/**
 * Type alias for promo code properties with specific generic types
 * @template T - Type for ID (default: string)
 * @template U - Type for item (default: Iitem)
 */
type TpromoCodeProps = IpromoCode<string, Iitem>;

/**
 * Represents a promo code in the system.
 * This class provides methods to manage promo codes, including CRUD operations.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TpromoCodeProps - The type definition for promo code properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link PromoCode} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const promoCode = await PromoCode.getAll();
 *
 * const promoCode = await PromoCode.filterAll({
 *
 * });
 *
 * const promoCode = await PromoCode.bulkDelete({
 *
 * });
 *
 * const promoCode = await PromoCode.getOne('resourceId');
 *
 * const promoCode = await PromoCode.remove();
 *
 * ```
 */
export class PromoCode
  extends DatabaseAuto {
  /** Base API endpoint for promo code operations */
  private static readonly baseUrl = '/promo-code';

  /** Unique reference ID for the promo code */
  readonly urId: TpromoCodeProps['urId'];

  /** Company ID that owns the promo code */
  readonly company?: TpromoCodeProps['company'];

  /** Promo code value */
  readonly code: TpromoCodeProps['code'];

  /** List of items associated with the promo code */
  readonly items: Item[];

  /** Number of times the promo code can be used */
  readonly timesToUse: TpromoCodeProps['timesToUse'];

  /** Number of times the promo code has been used */
  readonly timesUsed: TpromoCodeProps['timesUsed'];

  /** Discount percentage */
  readonly percentage: TpromoCodeProps['percentage'];

  /** Expiration date of the promo code */
  readonly expireAt?: TpromoCodeProps['expireAt'];

  /**
   * Constructs an instance of `PromoCode`.
   *
   * @param {IpromoCode} data - The promo code data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TpromoCodeProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      company: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Company ID must be a non-empty string'
      },
      items: {
        validate: (v) =>
          Array.isArray(v) &&
          v.length > 0 &&
          v.every(item =>
            item !== null &&
            item !== undefined),
        message: 'Items must be a non-empty array with valid items'
      },
      code: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Code must be a non-empty string'
      },
      timesToUse: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Times to use must be a non-negative number'
      },
      timesUsed: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Times used must be a non-negative number'
      },
      percentage: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Percentage must be a non-negative number'
      }
    });


    super(data);

    this.urId = data.urId;
    this.company = data.company;
    this.items = data.items.map((item) => new Item(item));
    this.code = data.code;
    this.timesToUse = data.timesToUse;
    this.timesUsed = data.timesUsed;
    this.percentage = data.percentage;
    this.expireAt = data.expireAt;
  }

  /**
   * Retrieves all promo codes of a specific type with pagination support.
   *
   * @param {number} [offset=0] - The starting point for pagination.
   * @param {number} [limit=20] - The number of items to retrieve.
   * @returns {Promise<{ count: number, dataArr: PromoCode[] }>} An object containing
   * the total count and an array of `PromoCode` instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TpromoCodeProps>>(`${PromoCode.baseUrl}/${offset}/${limit}`);
    const promoCodes = await lastValueFrom(observer$);

    return {
      count: promoCodes.count,
      dataArr: promoCodes.data.map((val) => new PromoCode(val))
    };
  }

  /**
   * Filters promo codes based on the provided filter properties.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving promo codes.
   * @returns {Promise<{ count: number, dataArr: PromoCode[] }>} An object containing
   * the total count and an array of `PromoCode` instances.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TpromoCodeProps>>(`${PromoCode.baseUrl}/filter`, filter);
    const promoCodes = await lastValueFrom(observer$);

    return {
      count: promoCodes.count,
      dataArr: promoCodes.data.map((val) => new PromoCode(val))
    };
  }

  /**
   * Retrieves a single promo code by its resource ID.
   *
   * @param {string} resourceId - The ID of the promo code to retrieve.
   * @returns {Promise<PromoCode>} The retrieved `PromoCode` instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TpromoCodeProps>(`${PromoCode.baseUrl}/${resourceId}`);
    const promoCode = await lastValueFrom(observer$);

    return new PromoCode(promoCode);
  }

  /**
   * Retrieves a single promo code by its code.
   *
   * @param {string} code - The code of the promo code to retrieve.
   * @returns {Promise<PromoCode>} The retrieved `PromoCode` instance.
   */
  static async getOneByCode(code: string) {
    const observer$ = CommerceClient.ehttp
      .makePost<TpromoCodeProps>(`${PromoCode.baseUrl}/get-by-code`, { code });
    const promoCode = await lastValueFrom(observer$);

    return new PromoCode(promoCode);
  }

  /**
   * Adds a new promo code.
   *
   * @param {Omit<IpromoCode, TbackEndGeneratedFields | 'company' | 'code' | 'timesUsed'>} payload -
   * The promo code data to add.
   * @returns {Promise<PromoCode>} The result of the addition operation.
   */
  static async add(payload: Omit<IpromoCode, TbackEndGeneratedFields | 'company' | 'code' | 'timesUsed'>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TpromoCodeProps>(`${PromoCode.baseUrl}`, payload);

    return new PromoCode(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple promo codes based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for deleting multiple promo codes.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the deletion operation.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${PromoCode.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current promo code.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the removal operation.
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${PromoCode.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
